import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Logger } from '@core/logger';
import {
	CreateEnvelopeOkResponse,
	UpdateEnvelopeResponse,
} from './signItEnvelopeResponseDto';
import { ProvisionBusinessUserOptions } from './provisionBusinessUserOptions';
import { ProvisionResponse } from './provisionResponseDto';
import { BaseHTTPHandlerService } from '@services/httpClientAbstract/httpClient.service.abstract';
import {
	ParticipantTypeEnum,
	SendEnvelopeRequest,
	SigningElement,
	SigningElementPackageViewModel,
	UpdateEnvelopeRequest,
} from '@models/e-signing';
import { PreparePackageForSigningOptions } from './preparePackageForSigningOptions';
import { ToastService } from 'src/app/ui/toast/toast.service';

@Injectable({ providedIn: 'root' })
export class SignItApiService extends BaseHTTPHandlerService {
	public basePath = 'closer/sign-it';
	private toastService = inject(ToastService);

	public provisionBusinessUser(body: ProvisionBusinessUserOptions) {
		return this.handleObservable(
			this.post<ProvisionResponse, ProvisionBusinessUserOptions>(
				'provision',
				body,
			),
			{
				toastSuccessMessage: 'Successfully connected to SignIT.',
				toastErrorMessage: 'Unable to connect to SignIt!',
				loggerInfo: 'Connect to SignIT integration response',
			},
		);
	}

	public updateEnvelope(
		matterId: string,
		envelopeId: string,
		request: UpdateEnvelopeRequest,
	) {
		return this.handleObservable(
			this.post<UpdateEnvelopeResponse>(
				`matter/${matterId}/envelope/${envelopeId}/update`,
				request,
			),
			{ loggerInfo: `Update Envelope response for envelope ${envelopeId}` },
		);
	}

	public sendEnvelope(
		matterId: string,
		envelopeId: string,
		request: SendEnvelopeRequest,
	) {
		return this.handleObservable(
			this.post<CreateEnvelopeOkResponse>(
				`matter/${matterId}/envelope/${envelopeId}/send`,
				request,
			),
			{ loggerInfo: `Send Envelope response for envelope ${envelopeId}` },
		);
	}

	public getPackageForDisplay(matterId: string, envelopeId: string) {
		return this.handleObservable(
			this.get<SigningElementPackageViewModel>(
				`matter/${matterId}/envelope/${envelopeId}/get-package-for-display`,
			),
			{
				loggerInfo: `Get Package for Display response for envelope ${envelopeId}`,
				toastErrorMessage: 'Unable to sync this envelope.',
			},
		);
	}

	public getAllPackagesForDisplay(matterId: string) {
		return this.handleObservable(
			this.get<SigningElementPackageViewModel[]>(
				`matter/${matterId}/get-all-packages-for-display`,
			),
			{
				loggerInfo: `Get All Packages for Display response for matter ${matterId}`,
				toastErrorMessage: 'Unable to sync envelopes.',
			},
		);
	}

	public cancelEnvelope(matterId: string, envelopeId: string) {
		return this.handleObservable(
			this.put<void>(
				`matter/${matterId}/envelope/${envelopeId}/cancel`,
				undefined,
				{ responseType: 'text' },
			),
			{
				loggerInfo: `Cancel Envelope response for envelope ${envelopeId}`,
				toastErrorMessage: 'Unable to cancel the envelope.',
			},
		);
	}

	public deleteEnvelope(matterId: string, envelopeId: string) {
		return this.handleObservable(
			this.delete<void>(`matter/${matterId}/envelope/${envelopeId}`, {
				responseType: 'text',
			}),
			{
				loggerInfo: `Delete Envelope response for envelope ${envelopeId}`,
				toastSuccessMessage: 'E-signing package successfully deleted.',
				toastErrorMessage: 'Unable to delete the envelope.',
			},
		);
	}

	public getCompletedEnvelopeDocument(matterId: string, envelopeId: string) {
		return this.handleObservable(
			this.get<{ fileName: string; base64Pdf: string }>(
				`matter/${matterId}/envelope/${envelopeId}/completed-document`,
			),
			{
				loggerInfo: `Get Completed Envelope Document response for envelope ${envelopeId}`,
			},
		);
	}

	public createDraftEnvelope(
		matterId: string,
		packageId: string,
		packageName: string,
	): Observable<string> {
		return this.handleObservable(
			this.post<
				{ envelopeId: string },
				{ matterId: string; packageId: string; packageName: string }
			>('create-draft-envelope', { matterId, packageId, packageName }).pipe(
				map((response) => response.envelopeId),
			),
			{
				loggerInfo: `Create Draft Envelope response for matter ${matterId}, package ${packageId}`,
				toastErrorMessage: 'Unable to create draft envelope.',
			},
		);
	}

	public preparePackageForSigning(
		matterId: string,
		packageId: string,
		participatingClientIds: PreparePackageForSigningOptions['participatingClientIds'],
		isCreateEnvelope = true,
	) {
		if (participatingClientIds.length === 0) {
			Logger.warn(
				`EnvelopeService: No client IDs provided for preparing package ${packageId} for signing.`,
			);
			return throwError(
				() =>
					new HttpErrorResponse({
						status: 400,
						statusText: 'Bad Request',
						error: 'No signors provided',
					}),
			);
		}

		if (
			!participatingClientIds.some(
				(p) => p.participantType === ParticipantTypeEnum.CLIENT,
			)
		) {
			Logger.warn(
				`EnvelopeService: No client participant types provided for preparing package ${packageId} for signing.`,
			);
			return throwError(
				() =>
					new HttpErrorResponse({
						status: 400,
						statusText: 'Bad Request',
						error: 'At least one client signor must be provided',
					}),
			);
		}

		return this.handleObservable(
			this.post<
				SigningElementPackageViewModel,
				PreparePackageForSigningOptions
			>(
				`matter/${matterId}/envelope/${packageId}/snapshot-package-for-signing?isCreateEnvelope=${isCreateEnvelope}`,
				{ participatingClientIds },
			),
			{
				loggerInfo: `Prepare Package for Signing response for envelope ${packageId}`,
			},
		);
	}

	/**
	 * The E Signing Blocks API Below
	 */

	public upsertBlock(
		matterId: string,
		packageId: string,
		block: SigningElement,
	) {
		return this.handleObservable(
			this.post<unknown, SigningElement>(
				`matter/${matterId}/package/${packageId}/blocks/upsert`,
				block,
			),
			{
				loggerInfo: `Upsert Block response for envelope ${packageId}`,
				toastErrorMessage: `Unable to save the ${block.elementType} block.`,
			},
		);
	}

	public deleteBlock(matterId: string, packageId: string, blockId: string) {
		return this.handleObservable(
			this.delete(`matter/${matterId}/package/${packageId}/blocks/${blockId}`),
			{
				loggerInfo: `Delete Block response for envelope ${packageId}`,
				toastErrorMessage: 'Unable to delete the block.',
			},
		);
	}

	private handleObservable<T>(
		observable: Observable<T>,
		options: {
			loggerInfo?: string;
			toastSuccessMessage?: string;
			toastErrorMessage?: string;
		},
	): Observable<T> {
		return observable.pipe(
			tap((response) => {
				if (options.toastSuccessMessage) {
					this.toastService.success(options.toastSuccessMessage);
				}
				if (options.loggerInfo) {
					Logger.info(
						`[${this.constructor.name}]: ${options.loggerInfo}`,
						response,
					);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				// Check if the error response has a specific message
				const errorMessage =
					error.status === 400 && error.error?.message
						? error.error.message
						: options.toastErrorMessage;

				if (errorMessage) {
					Logger.error(errorMessage);
					this.toastService.error(errorMessage);
				}
				return throwError(() => error);
			}),
		);
	}
}
